import { generateUuid } from "../../utils/id";
import type { BodyUUID, LightUUID, SceneLight, Vector3 } from "../../types/scene";

type LightParser = {
  lightClassDefaults: Record<string, Record<string, string>>;
  classParentMap: Record<string, string>;
  coerceUuidFromMjcfName: (value: string | null) => string | null;
  parsePassthroughAttributes: (
    node: Element,
    skipNames?: string[]
  ) => Record<string, string> | undefined;
};

const RESERVED_LIGHT_ATTRS = [
  "name",
  "class",
  "pos",
  "dir",
  "type",
  "directional",
  "active",
  "castshadow",
  "diffuse",
  "ambient",
  "specular",
  "range",
  "cutoff",
  "exponent",
  "attenuation",
  "intensity",
  "bulbradius",
  "mode",
  "target",
];

const parseNums = (value: string | null | undefined, expectedLen: number): number[] | null => {
  if (!value) return null;
  const nums = value.trim().split(/\s+/).map(Number);
  return nums.length >= expectedLen && nums.slice(0, expectedLen).every(Number.isFinite)
    ? nums.slice(0, expectedLen)
    : null;
};

const toVec3 = (value: number[] | null | undefined, fallback: Vector3): Vector3 =>
  value && value.length >= 3 ? ([value[0], value[1], value[2]] as Vector3) : [...fallback];

const normalizeDir = (value: Vector3): Vector3 => {
  const len = Math.hypot(value[0], value[1], value[2]);
  if (len < 1e-8) return [0, 0, -1];
  return [value[0] / len, value[1] / len, value[2] / len];
};

const parseBool = (value: string | null | undefined, fallback: boolean): boolean => {
  if (value == null) return fallback;
  const normalized = String(value).trim().toLowerCase();
  if (normalized === "true" || normalized === "1") return true;
  if (normalized === "false" || normalized === "0") return false;
  return fallback;
};

const parseNumber = (value: string | null | undefined, fallback: number): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const viewerVecFromMujoco = (pos: Vector3): Vector3 => [pos[0], pos[2], -pos[1]];

export const parseLightNode = (
  parser: LightParser,
  lightElem: Element,
  defaults: { kind: "fixed" | "mounted"; mountBodyUuid?: BodyUUID }
): SceneLight | null => {
  const name = lightElem.getAttribute("name") || `light_${generateUuid()}`;
  const stableUuid = parser.coerceUuidFromMjcfName(name);
  const uuid = (stableUuid || generateUuid()) as LightUUID;

  const className = lightElem.getAttribute("class");
  const getInheritedAttr = (attr: string): string | undefined => {
    if (!className) return undefined;
    let cur: string | null = className;
    const visited = new Set<string>();
    while (cur && !visited.has(cur)) {
      visited.add(cur);
      const value = parser.lightClassDefaults[cur]?.[attr];
      if (value != null) return value;
      cur = parser.classParentMap[cur] || null;
    }
    return undefined;
  };
  const getAttr = (attr: string): string | null | undefined =>
    lightElem.getAttribute(attr) ?? getInheritedAttr(attr);

  const rawPos = toVec3(parseNums(getAttr("pos"), 3), [0, 0, 0]);
  const rawDir = normalizeDir(toVec3(parseNums(getAttr("dir"), 3), [0, 0, -1]));
  const position = defaults.kind === "fixed" ? viewerVecFromMujoco(rawPos) : rawPos;
  const direction = defaults.kind === "fixed" ? normalizeDir(viewerVecFromMujoco(rawDir)) : rawDir;
  const directional = parseBool(getAttr("directional"), false);
  const type = (getAttr("type") || (directional ? "directional" : "spot")).trim();
  const targetBodyName = getAttr("target")?.trim() || undefined;
  const trackingMode = getAttr("mode")?.trim() || undefined;
  const passthroughAttrs =
    parser.parsePassthroughAttributes(lightElem, RESERVED_LIGHT_ATTRS) || undefined;

  return {
    uuid,
    name,
    kind: defaults.kind,
    mountType: defaults.kind === "mounted" ? "body" : undefined,
    mountBodyUuid: defaults.kind === "mounted" ? defaults.mountBodyUuid : undefined,
    position,
    direction,
    type,
    active: parseBool(getAttr("active"), true),
    castshadow: parseBool(getAttr("castshadow"), true),
    diffuse: toVec3(parseNums(getAttr("diffuse"), 3), [0.7, 0.7, 0.7]),
    ambient: toVec3(parseNums(getAttr("ambient"), 3), [0, 0, 0]),
    specular: toVec3(parseNums(getAttr("specular"), 3), [0.3, 0.3, 0.3]),
    range: Math.max(0, parseNumber(getAttr("range"), 10)),
    cutoff: Math.max(0, Math.min(180, parseNumber(getAttr("cutoff"), 45))),
    exponent: Math.max(0, parseNumber(getAttr("exponent"), 10)),
    attenuation: toVec3(parseNums(getAttr("attenuation"), 3), [1, 0, 0]),
    intensity: getAttr("intensity") != null ? parseNumber(getAttr("intensity"), 0) : undefined,
    bulbradius:
      getAttr("bulbradius") != null
        ? Math.max(0, parseNumber(getAttr("bulbradius"), 0.02))
        : undefined,
    trackingMode,
    targetBodyName,
    ...(passthroughAttrs ? { attrs: passthroughAttrs } : {}),
  };
};
